import React, { useState, useEffect } from 'react';
import '../styles/StreamModal.css';
import ChatBox from './ChatBox';

export function StreamModal({ streams = [], onClose, isOpen = true }) {
  const [activeTab, setActiveTab] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [showChat, setShowChat] = useState(false);
  // Track playlist positions per stream index when content contains multiple comma-separated URLs
  const [playlistPositions, setPlaylistPositions] = useState({});

  // Find the stream marked as active in the database
  useEffect(() => {
    if (streams.length > 0) {
      // Look for a stream with is_active = 1
      const activeIndex = streams.findIndex(s => s.is_active === 1);
      if (activeIndex !== -1) {
        setActiveTab(activeIndex);
      } else {
        setActiveTab(0);
      }
    }
  }, [streams]);

  // Handle tab switching with loading state
  const handleTabSwitch = (newTabIndex) => {
    setIsLoading(true);
    setActiveTab(newTabIndex);
    // ensure playlist position exists for this tab
    setPlaylistPositions(prev => ({ ...prev, [newTabIndex]: prev[newTabIndex] || 0 }));
    // Clear loading after a brief delay to show smooth transition
    const loadingTimer = setTimeout(() => {
      setIsLoading(false);
    }, 300);
    return () => clearTimeout(loadingTimer);
  };

  const handlePlaylistEnded = (streamIdx, listLength) => {
    setPlaylistPositions(prev => {
      const current = prev[streamIdx] || 0;
      const next = current + 1 >= listLength ? 0 : current + 1;
      return { ...prev, [streamIdx]: next };
    });
  };

  // When playlist index for the active tab changes, attempt to play the new video
  useEffect(() => {
    const pos = playlistPositions[activeTab];
    if (pos === undefined) return;

    const tryPlay = async () => {
      const el = document.getElementById(`stream-video-${activeTab}`);
      if (!el) return;

      // If it's a <video> element, try to play programmatically
      if (el.tagName === 'VIDEO') {
        try {
          await el.play();
          return;
        } catch (err) {
          // Autoplay failed — try muting and play
          try {
            el.muted = true;
            await el.play();
            // Attempt to unmute after a short delay (may still be blocked)
            setTimeout(() => { try { el.muted = false; } catch (e) {} }, 800);
          } catch (err2) {
            // give up - user interaction required
            console.warn('Autoplay blocked for playlist; user interaction required.');
          }
        }
      }
      // If it's an iframe (e.g., YouTube embed) we depend on embed params (autoplay) — nothing to do here
    };

    tryPlay();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playlistPositions[activeTab]]);

  // Update mute state for active video when activeTab changes
  useEffect(() => {
    // Use setTimeout to ensure DOM has updated
    const timer = setTimeout(() => {
      const activeVideoElement = document.getElementById(`stream-video-${activeTab}`);
      if (!activeVideoElement) return;

      if (activeVideoElement.tagName === 'VIDEO') {
        activeVideoElement.muted = false;
        activeVideoElement.volume = 1;
        activeVideoElement.currentTime = 0;
        activeVideoElement.play().catch(() => {});
      } else if (activeVideoElement.tagName === 'IFRAME') {
        const currentSrc = activeVideoElement.src;
        const isYouTube = currentSrc.includes('youtube.com/embed');
        const isVimeo = currentSrc.includes('vimeo.com');
        const isFacebook = currentSrc.includes('facebook.com');

        if (isYouTube || isVimeo) {
          try {
            const urlObj = new URL(currentSrc);
            urlObj.searchParams.set('autoplay', '1');
            urlObj.searchParams.set('mute', '0');
            const newSrc = urlObj.toString();
            
            if (activeVideoElement.src !== newSrc) {
              activeVideoElement.src = newSrc;
            }
          } catch (e) {
            console.error('Failed to update iframe URL', e);
          }
        } else if (isFacebook) {
          let newSrc = currentSrc.replace(/[?&]autoplay=\d+/, '');
          newSrc += (newSrc.includes('?') ? '&' : '?') + 'autoplay=1';
          if (activeVideoElement.src !== newSrc) {
            activeVideoElement.src = newSrc;
          }
        }
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [activeTab]);

  if (!isOpen || streams.length === 0) {
    return null;
  }

  const currentStream = streams[activeTab];

  const renderStreamContent = (stream, index) => {
    if (!stream) {
      return <div style={{ color: '#fff', textAlign: 'center', padding: '40px' }}>No stream available</div>;
    }

    const userData = JSON.parse(localStorage.getItem('app_user_data') || '{}');
    if (stream.is_private && !userData.is_vip) {
      return (
        <div style={{ color: '#fff', textAlign: 'center', padding: '40px', background: '#333', minHeight: '400px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <h2>🔒 Private Stream</h2>
          <p className="mb-4">This stream is for VIP members only.</p>
          <a href="/vip" className="btn btn-warning btn-lg fw-bold">Upgrade to VIP</a>
        </div>
      );
    }

    const { stream_type, is_currently_displayed, stream_name } = stream;

    // Normalize is_currently_displayed: handle raw string, JSON string, or object
    let displayedData = is_currently_displayed;
    if (typeof displayedData === 'string') {
      const trimmed = displayedData.trim();
      if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
        try {
          displayedData = JSON.parse(trimmed);
        } catch (e) {
          // leave as string if parse fails
        }
      }
    }

    if (typeof displayedData === 'object' && displayedData !== null) {
      // prefer explicitly marked field
      const field = displayedData.currently_displayed_field ||
        (displayedData.video_youtube_link ? 'video_youtube_link' :
         displayedData.stream_video_link ? 'stream_video_link' :
         displayedData.stream_video_path ? 'stream_video_path' :
         displayedData.stream_live_user_id ? 'stream_live_user_id' : null);

      var contentUrl = field && displayedData[field] ? displayedData[field] : (displayedData.stream_name || null);
      // If contentUrl is full object again, stringify fallback
    } else {
      var contentUrl = displayedData;
    }

    console.log(`[Stream ${index}] Name: ${stream_name}, Type: ${stream_type}, URL: `, contentUrl);

    if (!contentUrl) {
      // No content URL set for this tab
      return (
        <div style={{ 
          color: '#000', 
          textAlign: 'center', 
          padding: '40px', 
          background: '#333', 
          minHeight: '400px', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center' 
        }}>
          {stream_name} coming soon...
        </div>
      );
    }

    // 1. YouTube embed ID (video_youtube) - e.g., "dQw4w9WgXcQ" or a playlist URL
    if (stream_type === 'video_youtube') {
      const isActive = index === activeTab;
      const embedUrl = buildYouTubeEmbedUrl(contentUrl, isActive);
      return (
        <iframe
          key={`youtube-${index}-${isActive ? 'active' : 'inactive'}`}
          id={`stream-video-${index}`}
          width="100%"
          height="450"
          src={embedUrl}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          style={{ background: '#222' }}
        ></iframe>
      );
    }

    // 2. YouTube full URL (youtube) - extract ID and embed
    if (stream_type === 'youtube') {
      const isActive = index === activeTab;
      const embedUrl = buildYouTubeEmbedUrl(contentUrl, isActive);
      return (
        <iframe
          key={`youtube-${index}-${isActive ? 'active' : 'inactive'}`}
          id={`stream-video-${index}`}
          width="100%"
          height="450"
          src={embedUrl}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          style={{ background: '#222' }}
        ></iframe>
      );
    }

    // 3. Local video file (video) - e.g., "videos/1766981149064_Afro Pop Instrumental.mp4"
    if (stream_type === 'video') {
      const isActive = index === activeTab;
      // Support comma-separated playlist of local video URLs
      if (contentUrl && typeof contentUrl === 'string' && contentUrl.includes(',')) {
        const list = contentUrl.split(',').map(s => s.trim()).filter(Boolean);
        const pos = playlistPositions[index] || 0;
        const currentUrl = list[pos];
        return (
          <video
            key={`video-${index}-${isActive ? 'active' : 'inactive'}-${pos}`}
            id={`stream-video-${index}`}
            autoPlay={isActive}
            muted={!isActive}
            controls
            preload="metadata"
            style={{ width: '100%', height: '450px', background: '#222', objectFit: 'contain' }}
            onEnded={() => handlePlaylistEnded(index, list.length)}
          >
            <source src={currentUrl} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        );
      }

      return (
        <video
          key={`video-${index}-${isActive ? 'active' : 'inactive'}`}
          id={`stream-video-${index}`}
          autoPlay={isActive}
          muted={!isActive}
          controls
          preload="metadata"
          style={{ width: '100%', height: '450px', background: '#222', objectFit: 'contain' }}
        >
          <source src={contentUrl} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      );
    }

    // 4. Facebook/Vimeo embed link (video_link with embed URL)
    if (stream_type === 'video_link' && contentUrl && (contentUrl.includes('vimeo.com') || contentUrl.includes('embed') || contentUrl.includes('facebook.com'))) {
      const isActive = index === activeTab;

      if (contentUrl.includes('vimeo.com') || contentUrl.includes('embed')) {
        // Vimeo and other embeds
        const embedUrl = contentUrl.includes('?') 
          ? `${contentUrl}&autoplay=1&muted=${isActive ? 0 : 1}` 
          : `${contentUrl}?autoplay=1&muted=${isActive ? 0 : 1}`;
        return (
          <iframe
            key={`embed-${index}-${isActive ? 'active' : 'inactive'}`}
            id={`stream-video-${index}`}
            width="100%"
            height="450"
            src={embedUrl}
            frameBorder="0"
            allow="autoplay; clipboard-write; encrypted-media; picture-in-picture"
            allowFullScreen
            style={{ background: '#222' }}
          ></iframe>
        );
      }

      // Facebook - use Facebook plugin embed
      if (contentUrl.includes('facebook.com')) {
        const fbEmbedUrl = `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(contentUrl)}&width=500&show_text=false&autoplay=1`;
        return (
          <iframe
            key={`facebook-${index}-${isActive ? 'active' : 'inactive'}`}
            id={`stream-video-${index}`}
            src={fbEmbedUrl}
            width="100%"
            height="450"
            style={{
              border: 'none',
              overflow: 'hidden',
              background: '#000'
            }}
            scrolling="no"
            frameBorder="0"
            allowFullScreen={true}
            allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
          ></iframe>
        );
      }
    }

    // 5. External video link (video_link) - direct video URL
    if (stream_type === 'video_link') {
      const isCommaSeparatedYouTubeIds = contentUrl && typeof contentUrl === 'string' && contentUrl.includes(',')
        && contentUrl.split(',').map(part => part.trim()).filter(Boolean).every(part => {
          return /^(?:[A-Za-z0-9_-]{11})$/.test(part)
            || /youtu\.be\//.test(part)
            || /[?&]v=/.test(part);
        });

      // If the stored content is actually a YouTube watch URL or YouTube id inside JSON, build an embed URL
      if (contentUrl && (contentUrl.includes('youtube.com') || contentUrl.includes('youtu.be') || isCommaSeparatedYouTubeIds)) {
        const isActive = index === activeTab;
        const embedUrl = buildYouTubeEmbedUrl(contentUrl, isActive);
        return (
          <iframe
            key={`youtube-${index}-${isActive ? 'active' : 'inactive'}`}
            id={`stream-video-${index}`}
            width="100%"
            height="450"
            src={embedUrl}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            style={{ background: '#222' }}
          ></iframe>
        );
      }

      const isActive = index === activeTab;
      // Support comma-separated playlist of external video URLs
      if (contentUrl && typeof contentUrl === 'string' && contentUrl.includes(',')) {
        const list = contentUrl.split(',').map(s => s.trim()).filter(Boolean);
        const pos = playlistPositions[index] || 0;
        const currentUrl = list[pos];
        return (
          <video
            key={`external-${index}-${isActive ? 'active' : 'inactive'}-${pos}`}
            id={`stream-video-${index}`}
            autoPlay={isActive}
            muted={!isActive}
            controls
            preload="metadata"
            style={{ width: '100%', height: '450px', background: '#222', objectFit: 'contain' }}
            onEnded={() => handlePlaylistEnded(index, list.length)}
          >
            <source src={currentUrl} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        );
      }

      return (
        <video
          key={`external-${index}-${isActive ? 'active' : 'inactive'}`}
          id={`stream-video-${index}`}
          autoPlay={isActive}
          muted={!isActive}
          controls
          preload="metadata"
          style={{ width: '100%', height: '450px', background: '#222', objectFit: 'contain' }}
        >
          <source src={contentUrl} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      );
    }

    // 6. Live stream (live) - TODO: implement later
    if (stream_type === 'live') {
      return (
        <div style={{ 
          color: '#000', 
          textAlign: 'center', 
          padding: '40px', 
          background: '#333', 
          minHeight: '400px', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center' 
        }}>
          🔴 Live streaming will be available soon...
        </div>
      );
    }

    // Default fallback
    return (
      <div style={{ 
        color: '#000', 
        textAlign: 'center', 
        padding: '40px', 
        background: '#333', 
        minHeight: '400px', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center' 
      }}>
        {stream_name} - Unknown content type
      </div>
    );
  };

  return (
    <div className="stream-modal-overlay" onClick={onClose}>
      <div className="stream-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="stream-modal-header">
          <h2>Welcome to Alo@laba Festival</h2>
          <button className="stream-modal-close" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="stream-modal-tabs">
          {streams.map((stream, idx) => (
            <button
              key={idx}
              className={`stream-tab-btn ${activeTab === idx ? 'active' : ''}`}
              onClick={() => handleTabSwitch(idx)}
            >
              {stream.stream_name || `Stream ${idx + 1}`}
            </button>
          ))}
        </div>

        <div className="stream-modal-video-container">
          {renderStreamContent(currentStream, activeTab)}
        </div>

        {currentStream && currentStream.description && (
          <div className="stream-modal-description">
            {currentStream.description}
          </div>
        )}

        {showChat && (
          <div className="stream-modal-chat-container">
            <ChatBox streamId={currentStream?.id || activeTab} />
          </div>
        )}

        <div className="stream-modal-footer">
          <a href="#" className="stream-modal-link">
            🔴 LIVE Streaming: {currentStream?.title || currentStream?.stream_name || 'Alo@laba Festival'} 📺
          </a>
          <div className="d-flex gap-2 align-items-center">
            <a
              className="btn btn-warning btn-sm fw-bold shadow-sm rounded-pill px-3 d-flex align-items-center gap-1 text-dark"
              href="/donate"
              style={{
                background: 'linear-gradient(135deg, #f6d365 0%, #fda085 100%)',
                border: 'none',
                boxShadow: '0 4px 15px rgba(253, 160, 133, 0.4)',
                fontWeight: 600,
                padding: '10px 20px',
                borderRadius: '999px'
              }}
            >
              💝 Donate
            </a>
            <button 
              className={`btn ${showChat ? 'btn-secondary' : 'btn-outline-primary'}`}
              onClick={() => setShowChat(!showChat)}
              style={{ fontWeight: 600, padding: '10px 20px', borderRadius: '6px' }}
            >
              💬 {showChat ? 'Hide Chat' : 'Live Chat'}
            </button>
            <button className="stream-modal-continue-btn" onClick={onClose}>
              Continue to Site
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function buildYouTubeEmbedUrl(src, isActive) {
  if (!src) return '';
  const normalizedSrc = src.trim();
  const params = new URLSearchParams({
    autoplay: '1',
    mute: isActive ? '0' : '1',
    rel: '0',
    playsinline: '1',
    enablejsapi: '1'
  });

  try { params.set('origin', window.location.origin); } catch (e) {}

  const parts = normalizedSrc.split(',').map(p => p.trim()).filter(Boolean);
  
  let playlistId = null;
  let indexVal = null;
  const videoIds = [];

  for (const part of parts) {
    // Playlist ID extraction
    const listMatch = part.match(/[?&]list=([^&]+)/);
    if (listMatch && !playlistId) playlistId = listMatch[1];
    else if (/^PL[a-zA-Z0-9_-]{14,}$/.test(part) && !playlistId) playlistId = part;

    // Index extraction
    const idxMatch = part.match(/[?&]index=(\d+)/);
    if (idxMatch && !indexVal) indexVal = idxMatch[1];

    // Video ID extraction
    const vMatch = part.match(/[?&]v=([^&]+)/);
    const shortMatch = part.match(/youtu\.be\/([^?&]+)/);
    
    if (vMatch) {
      videoIds.push(vMatch[1]);
    } else if (shortMatch) {
      videoIds.push(shortMatch[1]);
    } else if (/^[a-zA-Z0-9_-]{11}$/.test(part)) {
      videoIds.push(part);
    }
  }

  if (indexVal) params.set('index', indexVal);

  let embedBase;

  if (playlistId) {
    embedBase = 'https://www.youtube.com/embed/videoseries';
    params.set('listType', 'playlist');
    params.set('list', playlistId);
  } else if (videoIds.length > 0) {
    embedBase = `https://www.youtube.com/embed/${videoIds[0]}`;
    if (videoIds.length > 1) {
      params.set('playlist', videoIds.slice(1).join(','));
    }
  } else {
    // Fallback if parsing fails (e.g. raw embed URL was provided)
    const fallbackMatch = normalizedSrc.match(/embed\/([^?&]+)/);
    if (fallbackMatch) {
      embedBase = `https://www.youtube.com/embed/${fallbackMatch[1]}`;
    } else {
      const fallbackId = normalizedSrc.split(',')[0].replace(/[^a-zA-Z0-9_-]/g, '').substring(0, 11);
      embedBase = `https://www.youtube.com/embed/${fallbackId}`;
    }
  }

  const finalUrl = `${embedBase}?${params.toString()}`;
  console.log('🎬 YouTube Embed Built:', { originalSrc: normalizedSrc, playlistId, videoIds, finalUrl });
  return finalUrl;
}
